import { useCallback, useRef, useState } from "react";
import { trademarkApi } from "../api/trademarkApi";

const CHUNK_SIZE = 10;

/**
 * A search row is thin: the register's list endpoint rarely carries goods &
 * services, owner addresses, the representative or the history. Those live
 * on the per-mark record, so this hook fetches them for just the rows the
 * user is looking at (one page, or the row open in the detail modal)
 * instead of one call per record up front.
 *
 * - Results are cached for the life of the page, so paging back is instant.
 * - Requests are chunked and sequential, so filling a page never turns
 *   into a burst the register would rate-limit.
 * - A mark that fails is reported, and can be retried by asking again —
 *   it never blocks the marks around it.
 * - `request()` resolves only once every mark it was asked about has
 *   settled — including marks another call is already fetching. That is
 *   what lets the Excel export say "get me these, then write the file"
 *   without racing the page's own fetch of the same rows.
 */
export default function useTrademarkDetails() {
  const [details, setDetails] = useState({});
  const [failed, setFailed] = useState({});
  const [loading, setLoading] = useState(() => new Set());

  // Mirrors `details` so an async caller (the Excel export) can read what
  // has arrived by the time its await resolves, not what was in scope when
  // it started.
  const detailsRef = useRef({});
  // application number -> the batch that is currently fetching it.
  const inflight = useRef(new Map());

  const request = useCallback(async (rows) => {
    const numbers = [
      ...new Set(
        (rows || [])
          .filter((row) => row?.source === "euipo" && row.applicationNumber)
          .map((row) => row.applicationNumber),
      ),
    ];

    const wanted = [];
    const running = new Set();
    numbers.forEach((number) => {
      if (detailsRef.current[number]) return;
      const batch = inflight.current.get(number);
      if (batch) running.add(batch);
      else wanted.push(number);
    });

    let ownBatch = Promise.resolve();

    if (wanted.length) {
      // A retry or a later visit starts clean rather than showing a stale
      // "failed" while the new attempt is in flight.
      setFailed((current) => {
        if (!wanted.some((number) => current[number])) return current;
        const next = { ...current };
        wanted.forEach((number) => delete next[number]);
        return next;
      });

      ownBatch = (async () => {
        for (let i = 0; i < wanted.length; i += CHUNK_SIZE) {
          const chunk = wanted.slice(i, i + CHUNK_SIZE);
          setLoading((current) => new Set([...current, ...chunk]));

          try {
            const response = await trademarkApi.getDetails(chunk);
            detailsRef.current = { ...detailsRef.current, ...response.details };
            setDetails(detailsRef.current);

            const missing = {};
            chunk.forEach((number) => {
              if (!response.details?.[number]) {
                missing[number] =
                  response.failed?.[number] ||
                  "The register returned no record.";
              }
            });
            if (Object.keys(missing).length)
              setFailed((current) => ({ ...current, ...missing }));
          } catch (err) {
            const missing = Object.fromEntries(
              chunk.map((number) => [number, err.message]),
            );
            setFailed((current) => ({ ...current, ...missing }));
          } finally {
            setLoading((current) => {
              const next = new Set(current);
              chunk.forEach((number) => next.delete(number));
              return next;
            });
          }
        }
      })();

      wanted.forEach((number) => inflight.current.set(number, ownBatch));
      ownBatch.finally(() =>
        wanted.forEach((number) => inflight.current.delete(number)),
      );
    }

    await Promise.all([ownBatch, ...running]);
  }, []);

  const retry = useCallback(
    (row) => {
      if (!row?.applicationNumber) return Promise.resolve();
      return request([row]);
    },
    [request],
  );

  /**
   * True while a row's full record is still on its way: an EU mark we
   * have neither received nor given up on. The views use this to show a
   * skeleton instead of "not published" for something merely not here yet.
   */
  const isPending = (row) =>
    row?.source === "euipo" &&
    Boolean(row.applicationNumber) &&
    !details[row.applicationNumber] &&
    !failed[row.applicationNumber];

  return {
    details,
    failed,
    loading,
    request,
    retry,
    isPending,
    getLoaded: () => detailsRef.current,
  };
}

/**
 * Lay a full record over a search row. The row's own fields win where they
 * exist (they came from the search itself); the record fills the gaps.
 */
export function mergeDetail(row, detail) {
  if (!detail) return row;

  const ownerNames = (detail.owners || [])
    .map((o) => o.name)
    .filter(Boolean)
    .join(", ");

  return {
    ...row,
    applicantName: row.applicantName || ownerNames,
    status: row.status || detail.status,
    markFeature: row.markFeature || detail.markFeature || detail.markKind,
    niceClasses: row.niceClasses?.length
      ? row.niceClasses
      : detail.niceClasses || [],
    filingDate: row.filingDate || detail.applicationDate,
    registrationDate: row.registrationDate || detail.registrationDate,
    expiryDate: row.expiryDate || detail.expiryDate,
    goodsAndServices: detail.goodsAndServices?.length
      ? detail.goodsAndServices
      : row.goodsAndServices,
    detail,
  };
}
